import { useCallback, useEffect, useState } from 'react'

import { Alert, Button, Dialog, Label, Select, Text } from '@/ui'
import {
  fetchPaymentConfigs,
  PUT_UPDATE_PAYMENT_CONFIG_KEY,
  putUpdatePaymentConfig,
} from '../payrollActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  Gusto_PaymentConfig,
  Gusto_PaymentSpeed,
} from '../generated_gusto_types'
import { useReselector } from '@/utils/sharedHooks'
import { getIsFetching } from '@/reducers/fetch'
import { PayrollError } from '../PayrollError'
import { selectPaySchedule } from '../payroll.selectors'
import { TypographyStyles } from '@/styles'

// Note 1-day is specifically omitted here
const dropdownValues = [
  { text: '2-day', value: '2-day' as const },
  { text: '4-day', value: '4-day' as const },
]

export const PaymentConfigSettings = () => {
  const dispatch = useAppDispatch()
  const [paymentConfig, setPaymentConfig] = useState<Gusto_PaymentConfig>()
  const [paymentSpeed, setPaymentSpeed] = useState<Gusto_PaymentSpeed>()
  const [modalOpen, setModalOpen] = useState(false)
  const paySchedule = useReselector(selectPaySchedule)
  const autoPayrollEnabled = Boolean(paySchedule?.auto_payroll)

  const isPosting = useReselector(getIsFetching, PUT_UPDATE_PAYMENT_CONFIG_KEY)

  useEffect(() => {
    const fetch = async () => {
      const res = await dispatch(fetchPaymentConfigs())

      if (res) {
        setPaymentConfig(res)
        setPaymentSpeed(res.payment_speed)
      }
    }

    fetch()
  }, [dispatch, paySchedule?.auto_payroll])

  const onSave = useCallback(async () => {
    const res = await dispatch(
      putUpdatePaymentConfig({ payment_speed: paymentSpeed })
    )

    if (res) {
      setPaymentConfig(res)
      setPaymentSpeed(res.payment_speed)
      setModalOpen(false)
    }
  }, [dispatch, paymentSpeed])

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-row w-full md:w-2/3">
          <Text variant={TypographyStyles.BODY_SM} className="mr-2">
            Payment Speed:
          </Text>
          <Text variant={TypographyStyles.BODY_SM}>
            {paymentConfig?.payment_speed === '1-day'
              ? 'Next-day'
              : paymentConfig?.payment_speed}
          </Text>
        </div>
        <div className="w-full md:w-1/3">
          {paymentSpeed && (
            <Button
              variant="link"
              onClick={() => setModalOpen(true)}
              disabled={autoPayrollEnabled}
              size="sm"
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      {autoPayrollEnabled && (
        <Alert variant="warning">
          Payment speed cannot be edited when Automatic Payroll is enabled
        </Alert>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Title>Update Payment Speed</Dialog.Title>
          <div className="flex flex-col gap-4">
            <PayrollError fetchKey={PUT_UPDATE_PAYMENT_CONFIG_KEY} />
            <Label className="mt-4 mb-2">Payment speed</Label>
            <Select
              options={dropdownValues}
              value={paymentSpeed}
              onValueChange={setPaymentSpeed}
            />
          </div>
          <Dialog.Footer className="mt-4">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={onSave} loading={isPosting} disabled={isPosting}>
              Save
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </div>
  )
}
